import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { fetchKpis, fetchSageExports } from '../api.js';
import {
  Alert,
  Badge,
  Button,
  Gauge,
  Meter,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  Stat,
} from '../ui/index.js';
import { int, pct, stamp } from '../lib/format.js';
import type { Tone } from '../ui/index.js';

/** Coverage bands: below half is a red flag, above 90 % is settled. */
function coverageTone(value: number): Tone {
  if (value >= 0.9) return 'good';
  if (value >= 0.5) return 'signal';
  return 'alarm';
}

const PIPELINE = [
  { step: '01', label: 'Ingestion', phase: 'P1', seat: 'KPI cat.', to: '/products' },
  { step: '02', label: 'Matching', phase: 'P2', seat: 'revue', to: '/workbench' },
  { step: '03', label: 'GTIN', phase: 'P2', seat: 'décision', to: '/workbench' },
  { step: '04', label: 'Sage', phase: 'P1', seat: 'export', to: '/sage-export' },
  { step: '05', label: 'Woo', phase: 'P3', seat: 'diffusion', to: '/woo-engine' },
] as const;

/** S01 — Dashboard (docs/09 §1): the KPI plate of docs/00-PLAN.md §6. */
export function DashboardPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const kpisQuery = useQuery({ queryKey: ['kpis'], queryFn: fetchKpis, refetchInterval: 30_000 });
  const exportsQuery = useQuery({ queryKey: ['sage-exports'], queryFn: fetchSageExports });

  if (kpisQuery.isPending) {
    return (
      <div>
        <PageHeader
          index="S01"
          title="Tableau de bord"
          purpose="Lecture des indicateurs du catalogue, du GTIN et du prix d'achat (docs/00-PLAN.md §6)."
        />
        <Panel>
          <PanelBody>
            <p className="text-sm text-ink-soft">Lecture des indicateurs…</p>
          </PanelBody>
        </Panel>
      </div>
    );
  }

  if (kpisQuery.isError) {
    return (
      <div>
        <PageHeader
          index="S01"
          title="Tableau de bord"
          purpose="Lecture des indicateurs du catalogue, du GTIN et du prix d'achat (docs/00-PLAN.md §6)."
        />
        <Alert tone="warn" title="KPIs indisponibles">
          {kpisQuery.error.message}. L&apos;ingestion du catalogue arrive en P1 — lancez
          d&apos;abord <code className="font-mono text-clay-600">pnpm db:migrate</code> et{' '}
          <code className="font-mono text-clay-600">pnpm db:seed</code>.
        </Alert>
      </div>
    );
  }

  const k = kpisQuery.data;
  const lastExport = exportsQuery.data?.[0];
  const publishedShare = k.productsTotal === 0 ? 0 : k.publishedVisible / k.productsTotal;
  const workload = k.openConflicts + k.pendingReviews;

  const seatValue: Record<string, string> = {
    '01': int(k.productsTotal),
    '02': int(k.pendingReviews),
    '03': int(k.gtinApproved),
    '04': lastExport === undefined ? '—' : `#${lastExport.id}`,
    '05': 'P3',
  };

  return (
    <div>
      <PageHeader
        index="S01"
        title="Tableau de bord"
        purpose="Toutes les barres de contrôle de la chaîne : catalogue miroir, couverture GTIN, prix d'achat réconciliés et dernier export Sage. Les lectures se rafraîchissent toutes les 30 secondes."
        meta={[
          { label: 'Produits miroir', value: int(k.productsTotal) },
          { label: 'SKUs vendables', value: int(k.sellableSkus) },
          { label: 'Charge à traiter', value: int(workload) },
        ]}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={kpisQuery.isFetching}
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['kpis'] });
              void queryClient.invalidateQueries({ queryKey: ['sage-exports'] });
            }}
          >
            {kpisQuery.isFetching ? 'Lecture…' : 'Rafraîchir'}
          </Button>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Stat
          label="Produits (miroir)"
          value={int(k.productsTotal)}
          hint="lignes WooCommerce ingérées (S04)"
          index="K1"
          to="/products"
        />
        <Stat
          label="Publiés & visibles"
          value={int(k.publishedVisible)}
          hint={`${pct(publishedShare)} du miroir`}
          fill={publishedShare}
          tone="scan"
          index="K2"
          to="/products?site=published"
        />
        <Stat
          label="SKUs vendables"
          value={int(k.sellableSkus)}
          hint="simple + variations, hors packs"
          index="K3"
          to="/products?flags=variant"
        />
        <Stat
          label="GTIN approuvés"
          value={int(k.gtinApproved)}
          hint={`couverture ${pct(k.gtinCoverage)}`}
          fill={k.gtinCoverage}
          tone={coverageTone(k.gtinCoverage)}
          index="K4"
          to="/workbench?tab=approved"
        />
        <Stat
          label="GTIN corrigés sur le site"
          value={int(k.siteAlignedGtin)}
          hint={
            k.pendingSiteCorrections > 0
              ? `${int(k.pendingSiteCorrections)} en attente d'envoi (« À corriger (Woo) »)`
              : 'site aligné sur les GTIN approuvés'
          }
          fill={k.gtinApproved === 0 ? 0 : k.siteAlignedGtin / k.gtinApproved}
          tone={k.pendingSiteCorrections > 0 ? 'signal' : 'good'}
          index="K4b"
          to="/corrections"
        />
        <Stat
          label="Produits du site sans SKU"
          value={k.siteMissingSku === null ? '—' : int(k.siteMissingSku)}
          hint={
            k.siteMissingSku === null
              ? 'premier miroir pas encore exécuté'
              : k.siteMissingSku > 0
                ? `à corriger sur le site · miroir ${k.mirrorLastRunAt !== null ? new Date(k.mirrorLastRunAt).toLocaleDateString('fr-FR') : '—'}`
                : `inventaire complet · miroir ${k.mirrorLastRunAt !== null ? new Date(k.mirrorLastRunAt).toLocaleDateString('fr-FR') : '—'}`
          }
          tone={k.siteMissingSku === null ? 'muted' : k.siteMissingSku > 0 ? 'alarm' : 'good'}
          index="K4c"
          to="/products?flags=no-sku"
        />
        <Stat
          label="Conflits ouverts"
          value={int(k.openConflicts)}
          hint="arbitrage requis (S03)"
          tone={k.openConflicts > 0 ? 'alarm' : 'good'}
          index="K5"
          to="/conflicts"
        />
        <Stat
          label="Revues en attente"
          value={int(k.pendingReviews)}
          hint="file GTIN Workbench (S02)"
          tone={k.pendingReviews > 0 ? 'signal' : 'good'}
          index="K6"
          to="/workbench"
        />
        <Stat
          label="Hors ligne / Corbeille Woo"
          value={int(k.deletedProductsCount ?? 0)}
          hint="non publiés, brouillons ou corbeille Woo"
          tone={(k.deletedProductsCount ?? 0) > 0 ? 'alarm' : 'good'}
          index="K7"
          to="/products?site=deleted"
        />
        <Stat
          label="Produits masqués (Woo)"
          value={int(k.hiddenProductsCount ?? 0)}
          hint="visibilité catalogue/recherche masquée"
          tone={(k.hiddenProductsCount ?? 0) > 0 ? 'signal' : 'good'}
          index="K8"
          to="/products?site=hidden"
        />
        <Stat
          label="En rupture de stock"
          value={int(k.outOfStockCount ?? 0)}
          hint="stock épuisé sur le site"
          tone={(k.outOfStockCount ?? 0) > 0 ? 'alarm' : 'good'}
          index="K9"
          to="/products?stock=outofstock"
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-4">
          <Panel>
            <PanelHeader index="05" title="Chaîne de traitement" hint="où en est chaque étage" />
            <PanelBody className="p-0">
              <ol className="divide-y divide-border">
                {PIPELINE.map((stage) => (
                  <li key={stage.step}>
                    <Link
                      to={stage.to}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream/50 transition-colors group cursor-pointer"
                    >
                      <span className="font-mono text-[10px] text-clay-600 group-hover:text-clay-800 font-bold">{stage.step}</span>
                      <span className="font-display text-[12px] font-semibold uppercase tracking-[0.14em] text-ink group-hover:text-clay-700">
                        {stage.label}
                      </span>
                      <Badge tone={stage.phase === 'P3' ? 'muted' : 'good'}>{stage.phase}</Badge>
                      <span className="tabular ml-auto font-mono text-sm text-ink">
                        {seatValue[stage.step] ?? '—'}
                      </span>
                      <span className="hidden w-24 text-right font-mono text-[10px] uppercase tracking-[0.14em] text-ink-faint sm:block group-hover:text-clay-600 transition-colors">
                        {stage.seat} →
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader
              index="K7"
              title="Prix d'achat réconciliés"
              hint="couverture des SKUs vendables"
            />
            <PanelBody>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="tabular font-display text-2xl font-semibold text-ink">
                  {pct(k.purchasePriceCoverage)}
                </span>
                <span className="text-xs text-ink-soft">
                  des SKUs vendables ont un prix d&apos;achat sélectionné
                </span>
              </div>
              <Meter
                value={k.purchasePriceCoverage}
                tone={coverageTone(k.purchasePriceCoverage)}
                className="mt-3"
              />
              <div className="mt-3 flex flex-wrap items-baseline gap-2 border-t border-border pt-3">
                <span className="tabular font-display text-xl font-semibold text-ink">
                  {pct(k.vatFileCoverage)}
                </span>
                <span className="text-xs text-ink-soft">
                  TVA source fichier (le reste : défaut 19 % à arbitrer)
                </span>
              </div>
              <Meter value={k.vatFileCoverage} tone={coverageTone(k.vatFileCoverage)} className="mt-2" />
              <p className="mt-3 text-xs leading-relaxed text-ink-soft">
                Alimenté par les listes fournisseurs (S06). Chaque offre retenue garde son{' '}
                <code className="font-mono text-ink">matched_by</code> (gtin / référence / nom) pour
                l&apos;audit du comptable.
              </p>
            </PanelBody>
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel>
            <PanelHeader index="G" title="Instruments de couverture" />
            <PanelBody>
              <div className="grid grid-cols-2 gap-2">
                <Gauge
                  value={k.gtinCoverage}
                  label="Couverture GTIN"
                  tone={coverageTone(k.gtinCoverage)}
                />
                <Gauge
                  value={k.purchasePriceCoverage}
                  label="Prix d'achat"
                  tone={coverageTone(k.purchasePriceCoverage)}
                />
                <Gauge
                  value={k.vatFileCoverage}
                  label="TVA fichier"
                  tone={coverageTone(k.vatFileCoverage)}
                />
              </div>
              <p className="mt-2 text-center text-[11px] text-ink-faint">
                100 % attendu avant toute diffusion vers le site (P3)
              </p>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader index="→" title="Prochaines actions" />
            <PanelBody className="space-y-2">
              <ActionRow
                to="/workbench"
                label="Traiter la file GTIN"
                value={`${int(k.pendingReviews)} produits`}
              />
              <ActionRow
                to="/conflicts"
                label="Arbitrer les conflits"
                value={`${int(k.openConflicts)} ouverts`}
              />
              <ActionRow to="/supplier-files" label="Importer une liste de prix" value="S06" />
              <ActionRow
                to="/sage-export"
                label={
                  lastExport === undefined
                    ? 'Générer le premier export Sage'
                    : `Dernier export #${lastExport.id}`
                }
                value={lastExport === undefined ? 'S09' : stamp(lastExport.createdAt)}
              />
            </PanelBody>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function ActionRow({
  to,
  label,
  value,
}: {
  readonly to: string;
  readonly label: string;
  readonly value: string;
}): React.ReactElement {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-lg border border-border bg-card px-3.5 py-2.5 shadow-xs transition-all duration-150 hover:border-herb-600/30 hover:bg-herb-50/20"
    >
      <span className="min-w-0 flex-1 truncate text-xs sm:text-sm font-medium text-ink group-hover:text-herb-800">
        {label}
      </span>
      <span className="tabular shrink-0 font-mono text-[11px] font-semibold text-ink-soft bg-cream px-2 py-0.5 rounded border border-border">
        {value}
      </span>
      <span aria-hidden="true" className="text-ink-ghost group-hover:text-herb-700 transition-transform group-hover:translate-x-0.5 text-xs">
        →
      </span>
    </Link>
  );
}
